import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, PlusIcon, XIcon } from 'lucide-react';
import { useStore } from '../../contexts/StoreContext';
import { LedFrame } from '../ui/LedFrame';
import { Vehicle } from '../../types/catalog';
import { getQuickQuote } from '../../utils/finance';
import { formatARS } from '../../utils/format';
import { vehicleLabel } from '../../utils/catalog';
const ease = [0.23, 1, 0.32, 1] as const;
const criteria = ['Precio de contado', 'Anticipo y cuota', 'TNA y CFT', 'Motor y consumo', 'Seguridad', 'Equipamiento'];
export function CompareCampaign() {
  const {
    vehicles,
    brands,
    compareIds,
    setCompareIds
  } = useStore();
  const published = useMemo(() => vehicles.filter(v => v.published), [vehicles]);
  const selected = compareIds.map(id => published.find(v => v.id === id)).filter((v): v is Vehicle => !!v);
  const setSlot = (i: number, id: string) => {
    const next = [...compareIds];
    if (id) next[i] = id;else next.splice(i, 1);
    setCompareIds(Array.from(new Set(next.filter(Boolean))));
  };
  return <section className="container-x py-12" aria-labelledby="compare-title" data-id="element-596" data-mp="27uynte8u10hj">
      <LedFrame radius={24} className="bg-ink" data-id="element-597" data-mp="22uavmp8vadlq">
        <div className="grid gap-8 rounded-[24px] p-6 sm:p-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-12 lg:p-10" data-id="element-598" data-mp="1xuy0lh7ue082">
          <div className="flex flex-col" data-id="element-599" data-mp="2viy9w0v782ps">
            <h2 id="compare-title" className="text-metal font-display text-3xl font-black leading-[0.98] tracking-tight sm:text-5xl" data-id="element-600" data-mp="2vhwwhpqpn44w">
              NO ELIJAS A CIEGAS.
              <br data-id="element-601" data-mp="2lrvenfoyahyf" />
              <span className="text-glow text-neon-hi" data-id="element-602" data-mp="2lr2cyt4umin5">COMPARÁ ANTES DE COMPRAR.</span>
            </h2>
            <p className="mt-4 max-w-md text-mist" data-id="element-603" data-mp="2gt0t45zlmfam">
              Poné hasta tres 0 km lado a lado, de la misma o de distintas marcas, y mirá las diferencias punto por punto.
            </p>
            <ul className="mt-6 flex flex-wrap gap-2" aria-label="Qué se compara" data-id="element-604" data-mp="39hfxlyl3tf7p">
              {criteria.map(c => <li key={c} className="rounded-full border border-silver-dark/40 bg-surface px-3 py-1.5 text-xs font-medium text-silver" data-id="element-605" data-mp="17ms72cxnkpei">
                  {c}
                </li>)}
            </ul>
          </div>

          <div data-id="element-606" data-mp="12tparx1hr1um">
            <div className="grid gap-3 sm:grid-cols-3" data-id="element-607" data-mp="12tfmcdw3h2r1">
              {[0, 1, 2].map(i => {
              const v = selected[i];
              const disabled = i > selected.length;
              const quote = v ? getQuickQuote(v, brands) : null;
              return <div key={i} className={`flex flex-col rounded-2xl border p-3 ${v ? 'border-neon/50 bg-surface' : 'border-dashed border-line bg-surface/40'}`} data-id="element-608" data-mp="ew9sofrhb1ls">
                    <AnimatePresence mode="wait" initial={false} data-id="element-609" data-mp="jvd026uxljwx">
                      {v ? <motion.div key={v.id} initial={{
                    opacity: 0,
                    scale: 0.96
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.96
                  }} transition={{
                    duration: 0.2,
                    ease
                  }} className="relative" data-id="element-610" data-mp="29asueg47512r">
                          <img src={v.images[0]} alt="" className="aspect-[4/3] w-full rounded-xl object-cover" data-id="element-611" data-mp="2x6nufjnbkpf7" />
                          <button type="button" onClick={() => setSlot(i, '')} aria-label={`Quitar ${v.model}`} className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-ink/80 text-white hover:bg-neon" data-id="element-612" data-mp="2scjpt71j0tha">
                            <XIcon className="h-3.5 w-3.5" data-id="element-613" data-mp="3c3yevihxx5n2" />
                          </button>
                          <p className="mt-2 text-[11px] text-mist" data-id="element-614" data-mp="1lqz72r65lodv">{brands.find(b => b.id === v.brandId)?.name}</p>
                          <p className="truncate font-display font-bold leading-tight" data-id="element-615" data-mp="1hqfbvt554uud">{v.model}</p>
                          <p className="mt-1 text-sm font-semibold" data-id="element-616" data-mp="1j5i1ap7nn08u">{formatARS(v.price)}</p>
                          <p className="text-xs text-neon-hi" data-id="element-617" data-mp="1o3b6bex81o7j">{quote?.sim ? `${quote.term} × ${formatARS(quote.sim.firstInstallment)}` : 'Cuota a confirmar'}</p>
                        </motion.div> : <motion.div key={`empty-${i}`} initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} transition={{
                    duration: 0.15
                  }} className="grid aspect-[4/3] place-items-center rounded-xl bg-panel/60" aria-hidden data-id="element-618" data-mp="1x36mxz9jqbey">
                          <PlusIcon className="h-6 w-6 text-mist" data-id="element-619" data-mp="gq9gn3zer2bb" />
                        </motion.div>}
                    </AnimatePresence>
                    <label htmlFor={`hc-${i}`} className="sr-only" data-id="element-620" data-mp="e6mi5dj9c7rw">Vehículo {i + 1}</label>
                    <select id={`hc-${i}`} className="input mt-3 h-10 text-sm" value={v?.id ?? ''} disabled={disabled} onChange={e => setSlot(i, e.target.value)} data-id="element-621" data-mp="97s0wg0xur9w">
                      <option value="" data-id="element-622" data-mp="3rdehaym1utfd">{disabled ? 'Elegí el anterior' : `Vehículo ${i + 1}`}</option>
                      {published.filter(x => x.id === v?.id || !compareIds.includes(x.id)).map(x => <option key={x.id} value={x.id} data-id="element-623" data-mp="3ho4o1ffgz4po">{vehicleLabel(x, brands)}</option>)}
                    </select>
                  </div>;
            })}
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-624" data-mp="38ozqocfn9hf3">
              <p className="text-sm text-mist" aria-live="polite" data-id="element-625" data-mp="33pwuzilk0njs">
                {selected.length < 2 ? `Elegí ${selected.length === 0 ? 'dos vehículos' : 'un vehículo más'} para comparar.` : `${selected.length} vehículos listos para comparar.`}
              </p>
              {selected.length >= 2 ? <Link to="/comparar" className="btn btn-primary group h-12 px-6" data-id="element-626" data-mp="2k4d801ie3xmy">
                  Comparar ahora
                  <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-1" aria-hidden data-id="element-627" data-mp="3cqxy6yn4q3ox" />
                </Link> : <button type="button" disabled className="btn btn-primary h-12 px-6 opacity-50" data-id="element-628" data-mp="3hrvcv18xkflo">
                  Comparar ahora
                </button>}
            </div>
          </div>
        </div>
      </LedFrame>
    </section>;
}